import WidgetLoadingAnimation from 'components/Widgets/WidgetLoadingAnimation';
import { usePerms } from 'contexts/PermsContext';
import AlertList from 'features/Weather/features/WeatherAlerts/features/AlertList/index.web';
import { selectAlertList } from 'features/Weather/features/WeatherAlerts/features/AlertList/state/selectors/alertListSelectors';
import { setAlertList } from 'features/Weather/features/WeatherAlerts/features/AlertList/state/slices/alertListSlice';
import WeatherAlertsWidget from 'features/Weather/features/WeatherAlerts/index.web.widget';
import useLocation from 'hooks/useLocation';
import { useEffect, useState } from 'react';
import { getAlertWithLocationDetails } from 'features/Weather/features/WeatherAlerts/utils/alertLocation.utils';
import { Container } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useGetAllActiveAlertsQuery } from 'services/alerts/alerts.api';

const Alerts = ({ dimensions, sidebarOpen }) => {
  const { data: allActiveAlerts = [] } = useGetAllActiveAlertsQuery();
  const { locations } = useLocation();
  const effectiveLocation = locations.effectiveLocation;
  const alertList = useSelector(selectAlertList);
  const dispatch = useDispatch();
  const [fetching, setFetching] = useState(true);
  const perms = usePerms();
  const navigate = useNavigate();

  const canDeleteAlerts = perms.testPermission([8, 9, 10, 11, 12, 13], ['DeleteAlerts']);

  useEffect(() => {
    if (allActiveAlerts?.length) {
      dispatch(setAlertList(allActiveAlerts));
      setFetching(false);
    }
  }, [allActiveAlerts, dispatch]);

  useEffect(() => {
    (async () => {
      if (!(await perms.validatePermissions(['ViewAlerts']))) {
        navigate('/404');
        return;
      }
    })();
  }, [navigate, perms]);

  useEffect(() => {
    // Checks if the alerts list is empty or if all alerts already have distanceToUser calculated
    if (
      !alertList.length ||
      alertList.every((alert) => alert.distanceToUser !== undefined || !effectiveLocation?.coords)
    ) {
      return;
    }

    let canceled = false; // Flag to prevent state updates if the component unmounts or dependencies change

    // Injects needed information into the alert object
    const processedAlerts = alertList.map((alert) =>
      getAlertWithLocationDetails(alert, effectiveLocation.coords),
    );
    if (!canceled) dispatch(setAlertList(processedAlerts));

    // Cleanup function ensures if dependencies change or component unmounts the async operation does not attempt to update state
    return () => {
      canceled = true;
    };
  }, [alertList, effectiveLocation, dispatch]);

  if (dimensions?.length && perms.testPermission([8, 9, 10, 11, 12, 13], ['WeatherAlerts'])) {
    return (
      <WeatherAlertsWidget
        isLoading={fetching}
        dimensions={dimensions}
        sidebarOpen={sidebarOpen}
        widgetType="list"
        route="getAllActiveAlerts"
        canDeleteAlerts={canDeleteAlerts}
      />
    );
  }

  if (perms.testPermission([8, 9, 10, 11, 12, 13], ['WeatherAlerts']))
    return (
      <Container fluid className="py-3">
        <div style={{ height: 'calc(100% - 20px)' }}>
          {fetching ? (
            <WidgetLoadingAnimation />
          ) : (
            <AlertList
              title={'Active Weather Alerts'}
              perms={perms}
              canDeleteAlerts={canDeleteAlerts}
            />
          )}
        </div>
      </Container>
    );
};

export default Alerts;
